// path: frontend/src/api/queries/employees.js
/**
 * Хуки сотрудников. useEmployeesQuery — list-эндпоинт GET /employees
 * возвращает либо массив, либо постраничный конверт { items, meta } —
 * нормализуем через unwrapItems (utils/dbShape.js) прямо в хуке.
 * Escape hatch — свой select через options.
 *
 * useEmployeeQuery — одиночный сотрудник (GET /employees/:id), DTO as-is,
 * без select. useAssignableEmployeesQuery — тоже list, но не трогаем
 * (отдельная область: assignable обычно возвращает полный массив).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { unwrapItems } from "../../utils/dbShape";

export const EMPLOYEES_KEY = ["employees"];

export function useEmployeesQuery(options = {}) {
  return useQuery({
    queryKey: EMPLOYEES_KEY,
    queryFn: () => api.get("/employees"),
    select: unwrapItems,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useAssignableEmployeesQuery(options = {}) {
  return useQuery({
    queryKey: ["employees", "assignable"],
    queryFn: () => api.get("/employees/assignable"),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useEmployeeQuery(id, options = {}) {
  return useQuery({
    queryKey: ["employee", id],
    queryFn: () => api.get(`/employees/${id}`),
    enabled: !!id,
    ...options,
  });
}

export function useCreateEmployeeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/employees", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: EMPLOYEES_KEY }),
  });
}

export function useUpdateEmployeeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => api.patch(`/employees/${id}`, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: EMPLOYEES_KEY });
      qc.invalidateQueries({ queryKey: ["employee", id] });
    },
  });
}

export function useUpdateSelfMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch("/employees/me", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: EMPLOYEES_KEY });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useUploadAvatarMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }) => {
      const formData = new FormData();
      formData.append("file", file);
      return api.post(`/employees/${id}/avatar`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: EMPLOYEES_KEY });
      qc.invalidateQueries({ queryKey: ["employee", id] });
    },
  });
}

export function useDeleteAvatarMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/employees/${id}/avatar`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: EMPLOYEES_KEY });
      qc.invalidateQueries({ queryKey: ["employee", id] });
    },
  });
}

export function useFireEmployeeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/employees/${id}`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: EMPLOYEES_KEY });
      qc.invalidateQueries({ queryKey: ["employee", id] });
    },
  });
}

export function useRestoreEmployeeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/employees/${id}/restore`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: EMPLOYEES_KEY });
      qc.invalidateQueries({ queryKey: ["employee", id] });
    },
  });
}
